'use client';

import { c as _c } from "react/compiler-runtime";
import type { PendingLineComment, PendingLineCommentReply } from '@/data/v2/pr/pending-comments-types';
import type { ReviewSubmissionResponse } from '@/lib/github/review-submission-types';
import type { MergeabilityStatusResponse } from '@/lib/hooks/mergeability-status/query-data';
import type { PrV2CommentResponse, PrV2DiscussionResponse, PrV2ReviewResponse, PrV2ReviewThread, PrV2SummaryResponse } from '@/lib/pr-v2/types';
import type { Review } from '@/types/github';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { useSessionStorage } from 'usehooks-ts';
import type { PromotedPendingComment, ReviewMutationContext, ReviewTypeValue, SubmitReviewMutationVariables } from './review-submit-types';
import { submitPrV2Review } from '../fetch-pr-v2';
import { linkPrV2LocalCommentIds, removePrV2LocalCommentsByLookupIds, removePrV2LocalReviewsByLookupIds, removePrV2LocalThreadsByLookupIds, stagePrV2OptimisticComments, stagePrV2OptimisticReviews, stagePrV2OptimisticThreads } from '../local-comment-model';
import { prV2ReviewSubmitMutationKey } from '../mutation-keys';
import { usePendingCommentsStore } from '../pending-comments-store';
import { prV2CommentsQueryKey, prV2DiscussionQueryKey, prV2ReviewsQueryKey, prV2ReviewThreadsQueryKey, prV2SummaryQueryKey } from '../query-keys';
import { appendOptimisticComment, appendOptimisticThread, appendOptimisticTimelineItem, appendReplyToThread, applyReviewToMergeability, buildOptimisticReplyParentIdRemap, getRequestedReviewerForLogin, remapOptimisticReplyParentsInComments, remapOptimisticReplyParentsInDiscussion, removeOptimisticCommentsById, removeOptimisticThreadNodesByCommentId, removeOptimisticTimelineItemsById, removeRequestedReviewerFromSummary, removeReviewById, restoreRequestedReviewerInSummary } from './review-submit-cache';
import { buildOptimisticPromotedComment, buildOptimisticReview, buildOptimisticReviewCommentTimelineItem, buildOptimisticReviewThread, buildOptimisticReviewTimelineItem, normalizeReviewBody } from './review-submit-optimistic';
import { scheduleReviewFallbackInvalidation } from './review-submit-state';
import { filterPendingCommentsForPr, formatSubmitReviewComments, remapFailedPendingCommentParent } from './review-submit-utils';
import { createFirstReviewDesktopToast } from '@/components/desktop-promotion/first-review-desktop-toast';
import { canShowOtherAppPromotion, noteReviewSubmitted } from '@/components/install-prompts/install-prompt-store';
import { useToDesktop } from '@/components/providers/todesktop-auth-provider';
import { patchInboxPullRequest } from '@/data/pull-requests/patch-inbox-pull-request';
import { clearPendingOptimisticMutation } from '@/data/pull-requests/pending-optimistic-mutations';
import { inboxV2MergedPullRequestsQueryKeyPrefix, inboxV2OpenPullRequestsQueryKeyPrefix } from '@/data/pull-requests/query-keys';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { mergeabilityStatusQueryKey } from '@/lib/hooks/mergeability-status/keys';
import { toPendingGithubCommentId } from '@/lib/pr-v2/pending-comment-ids';
import { registerStableViewKey, transferStableViewKeyToId } from '@/lib/pr-v2/stable-view-keys';
import { STORAGE_KEYS } from '@/lib/storage-keys';
import { usePrStore } from '@/lib/stores/pr-store';
import { useAuth } from '@/providers/auth-provider';
import { ReviewType } from '@/types/github';
export { remapFailedPendingCommentParent } from './review-submit-utils';

/**
 * Stable submission ID for server-side review dedupe. The ID is generated on the
 * first submit attempt and reused across retries after errors, since a false
 * failure can leave GitHub having accepted the review even though the client saw
 * an error. It is cleared only on success so the next distinct submission gets a
 * fresh ID. The server uses this ID as the key into its KV idempotency ledger.
 */
function useSubmissionIdStableAcrossRetries() {
  const submissionIdRef = useRef<string | null>(null);
  const getOrCreateSubmissionId = () => {
    submissionIdRef.current ??= crypto.randomUUID();
    return submissionIdRef.current;
  };
  const startNewSubmissionIdAfterSuccess = () => {
    submissionIdRef.current = null;
  };
  return {
    getOrCreateSubmissionId,
    startNewSubmissionIdAfterSuccess
  };
}
export function usePrV2ReviewSubmit(owner, repo, number) {
  const $ = _c(142);
  const queryClient = useQueryClient();
  const router = useRouter();
  const {
    isDesktopApp
  } = useToDesktop();
  const {
    installationId
  } = usePrStore();
  const {
    user
  } = useAuth();
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = STORAGE_KEYS.PR_REVIEW_COMMENT(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const [commentText, setCommentText, removeCommentText] = useSessionStorage(t0, "");
  const pendingComments = usePendingCommentsStore(_temp);
  const clearPendingComments = usePendingCommentsStore(_temp2);
  const addPendingComment = usePendingCommentsStore(_temp3);
  const {
    getOrCreateSubmissionId,
    startNewSubmissionIdAfterSuccess
  } = useSubmissionIdStableAcrossRetries();
  let t1;
  if ($[4] !== owner) {
    t1 = owner.toLowerCase();
    $[4] = owner;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const normalizedOwner = t1;
  let t2;
  if ($[6] !== repo) {
    t2 = repo.toLowerCase();
    $[6] = repo;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const normalizedRepo = t2;
  const pullNumber = Number(number);
  const prScopeKey = `${owner}/${repo}/${number}`;
  let t3;
  if ($[8] !== user) {
    t3 = user?.user_metadata && typeof user.user_metadata === "object" ? user.user_metadata as Record<string, unknown> : {};
    $[8] = user;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const authMetadata = t3;
  let t4;
  if ($[10] !== authMetadata.user_name || $[11] !== user) {
    t4 = typeof authMetadata.user_name === "string" && authMetadata.user_name.trim() || typeof user?.email === "string" && user.email.trim() || "you";
    $[10] = authMetadata.user_name;
    $[11] = user;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const userLogin = t4;
  const userAvatarUrl = typeof authMetadata.avatar_url === "string" ? authMetadata.avatar_url : null;
  let t5;
  if ($[13] !== number || $[14] !== owner || $[15] !== repo) {
    t5 = prV2SummaryQueryKey(owner, repo, number);
    $[13] = number;
    $[14] = owner;
    $[15] = repo;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const summaryKey = t5;
  let t6;
  if ($[17] !== number || $[18] !== owner || $[19] !== repo) {
    t6 = prV2DiscussionQueryKey(owner, repo, number);
    $[17] = number;
    $[18] = owner;
    $[19] = repo;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const discussionKey = t6;
  let t7;
  if ($[21] !== number || $[22] !== owner || $[23] !== repo) {
    t7 = prV2ReviewsQueryKey(owner, repo, number);
    $[21] = number;
    $[22] = owner;
    $[23] = repo;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const reviewsKey = t7;
  let t8;
  if ($[25] !== number || $[26] !== owner || $[27] !== repo) {
    t8 = prV2CommentsQueryKey(owner, repo, number);
    $[25] = number;
    $[26] = owner;
    $[27] = repo;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const commentsKey = t8;
  let t9;
  if ($[29] !== number || $[30] !== owner || $[31] !== repo) {
    t9 = prV2ReviewThreadsQueryKey(owner, repo, number);
    $[29] = number;
    $[30] = owner;
    $[31] = repo;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const reviewThreadsKey = t9;
  let t10;
  if ($[33] !== number || $[34] !== owner || $[35] !== repo) {
    t10 = mergeabilityStatusQueryKey(owner, repo, number, "v2");
    $[33] = number;
    $[34] = owner;
    $[35] = repo;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const mergeabilityKey = t10;
  let t11;
  if ($[37] !== number || $[38] !== owner || $[39] !== repo) {
    t11 = prV2ReviewSubmitMutationKey(owner, repo, number);
    $[37] = number;
    $[38] = owner;
    $[39] = repo;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  const mutationKey = t11;
  let t12;
  if ($[41] !== normalizedOwner || $[42] !== normalizedRepo || $[43] !== pullNumber) {
    t12 = () => filterPendingCommentsForPr(usePendingCommentsStore.getState().pendingComments, normalizedOwner, normalizedRepo, pullNumber);
    $[41] = normalizedOwner;
    $[42] = normalizedRepo;
    $[43] = pullNumber;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  const getPendingCommentsSnapshot = t12;
  let t13;
  if ($[45] !== normalizedOwner || $[46] !== normalizedRepo || $[47] !== pendingComments || $[48] !== pullNumber) {
    t13 = filterPendingCommentsForPr(pendingComments, normalizedOwner, normalizedRepo, pullNumber);
    $[45] = normalizedOwner;
    $[46] = normalizedRepo;
    $[47] = pendingComments;
    $[48] = pullNumber;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  const prPendingComments = t13;
  let t14;
  if ($[50] !== commentsKey || $[51] !== discussionKey || $[52] !== mergeabilityKey || $[53] !== queryClient || $[54] !== reviewThreadsKey || $[55] !== reviewsKey || $[56] !== summaryKey) {
    t14 = async () => {
      await Promise.all([queryClient.invalidateQueries({
        queryKey: summaryKey
      }), queryClient.invalidateQueries({
        queryKey: discussionKey
      }), queryClient.invalidateQueries({
        queryKey: reviewsKey
      }), queryClient.invalidateQueries({
        queryKey: commentsKey
      }), queryClient.invalidateQueries({
        queryKey: reviewThreadsKey
      }), queryClient.invalidateQueries({
        queryKey: mergeabilityKey
      }), queryClient.invalidateQueries({
        queryKey: inboxV2OpenPullRequestsQueryKeyPrefix()
      }), queryClient.invalidateQueries({
        queryKey: inboxV2MergedPullRequestsQueryKeyPrefix()
      })]);
    };
    $[50] = commentsKey;
    $[51] = discussionKey;
    $[52] = mergeabilityKey;
    $[53] = queryClient;
    $[54] = reviewThreadsKey;
    $[55] = reviewsKey;
    $[56] = summaryKey;
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  const invalidateAfterMutation = t14;
  let t15;
  if ($[58] !== commentsKey || $[59] !== discussionKey || $[60] !== mergeabilityKey || $[61] !== queryClient || $[62] !== reviewThreadsKey || $[63] !== reviewsKey || $[64] !== summaryKey) {
    t15 = async () => {
      await Promise.all([queryClient.invalidateQueries({
        queryKey: summaryKey,
        refetchType: "none"
      }), queryClient.invalidateQueries({
        queryKey: discussionKey,
        refetchType: "none"
      }), queryClient.invalidateQueries({
        queryKey: reviewsKey,
        refetchType: "none"
      }), queryClient.invalidateQueries({
        queryKey: commentsKey,
        refetchType: "none"
      }), queryClient.invalidateQueries({
        queryKey: reviewThreadsKey,
        refetchType: "none"
      }), queryClient.invalidateQueries({
        queryKey: mergeabilityKey,
        refetchType: "none"
      }), queryClient.invalidateQueries({
        queryKey: inboxV2OpenPullRequestsQueryKeyPrefix(),
        refetchType: "none"
      }), queryClient.invalidateQueries({
        queryKey: inboxV2MergedPullRequestsQueryKeyPrefix(),
        refetchType: "none"
      })]);
    };
    $[58] = commentsKey;
    $[59] = discussionKey;
    $[60] = mergeabilityKey;
    $[61] = queryClient;
    $[62] = reviewThreadsKey;
    $[63] = reviewsKey;
    $[64] = summaryKey;
    $[65] = t15;
  } else {
    t15 = $[65];
  }
  const softInvalidateAfterMutation = t15;
  let t16;
  if ($[66] !== installationId || $[67] !== isDesktopApp || $[68] !== number || $[69] !== owner || $[70] !== repo || $[71] !== router) {
    t16 = async variables => {
      const promise = submitPrV2Review(owner, repo, number, {
        submissionId: variables.submissionId,
        review: {
          comment: variables.comment,
          reviewType: variables.reviewType
        },
        comments: formatSubmitReviewComments(variables.pendingCommentsSnapshot)
      });
      toast.promise(promise, {
        loading: "Submitting review...",
        success: result => {
          if (result.isFirstCubicUiReview && !isDesktopApp && canShowOtherAppPromotion()) {
            return createFirstReviewDesktopToast({
              installationId,
              source: "pr_v2",
              onGetApp: () => router.push("/download")
            });
          }
          return {
            message: variables.reviewType === ReviewType.APPROVE ? "Pull request approved" : "Review submitted"
          };
        },
        error: _temp4
      });
      return promise;
    };
    $[66] = installationId;
    $[67] = isDesktopApp;
    $[68] = number;
    $[69] = owner;
    $[70] = repo;
    $[71] = router;
    $[72] = t16;
  } else {
    t16 = $[72];
  }
  let t17;
  if ($[73] !== clearPendingComments || $[74] !== commentsKey || $[75] !== discussionKey || $[76] !== mergeabilityKey || $[77] !== number || $[78] !== owner || $[79] !== prScopeKey || $[80] !== queryClient || $[81] !== repo || $[82] !== reviewThreadsKey || $[83] !== reviewsKey || $[84] !== summaryKey || $[85] !== userAvatarUrl || $[86] !== userLogin) {
    t17 = async variables_0 => {
      await Promise.all([queryClient.cancelQueries({
        queryKey: summaryKey
      }), queryClient.cancelQueries({
        queryKey: discussionKey
      }), queryClient.cancelQueries({
        queryKey: reviewsKey
      }), queryClient.cancelQueries({
        queryKey: commentsKey
      }), queryClient.cancelQueries({
        queryKey: reviewThreadsKey
      }), queryClient.cancelQueries({
        queryKey: mergeabilityKey
      })]);
      const previousSummary = queryClient.getQueryData(summaryKey);
      const previousMergeability = queryClient.getQueryData(mergeabilityKey);
      const requestedReviewerToRestore = getRequestedReviewerForLogin(previousSummary, userLogin);
      const timestamp = new Date().toISOString();
      const normalizedReviewBody = normalizeReviewBody(variables_0.comment, variables_0.reviewType, variables_0.pendingCommentsSnapshot.length);
      const optimisticReview = buildOptimisticReview({
        reviewType: variables_0.reviewType,
        body: normalizedReviewBody,
        userLogin,
        userAvatarUrl,
        commitId: previousSummary?.pullRequest.headSha ?? null,
        timestamp
      });
      const optimisticInboxReview = {
        id: optimisticReview.githubReviewId,
        node_id: optimisticReview.githubReviewNodeId ?? optimisticReview.githubReviewId,
        user: {
          login: userLogin,
          id: 0,
          avatar_url: userAvatarUrl ?? "",
          html_url: "",
          type: "User"
        },
        body: optimisticReview.body ?? "",
        state: optimisticReview.state,
        html_url: "",
        pull_request_url: "",
        author_association: "CONTRIBUTOR",
        _links: {
          html: {
            href: ""
          },
          pull_request: {
            href: ""
          }
        },
        submitted_at: timestamp,
        commit_id: optimisticReview.commitId ?? ""
      } as unknown as Review;
      const promotedPendingComments = [];
      const optimisticCommentIdByPendingId = new Map();
      let didApplyOptimisticSummary = false;
      stagePrV2OptimisticReviews(prScopeKey, [optimisticReview]);
      queryClient.setQueryData(reviewsKey, current => [...(current ?? []), optimisticReview]);
      queryClient.setQueryData(discussionKey, current_0 => appendOptimisticTimelineItem(current_0, buildOptimisticReviewTimelineItem({
        optimisticReview,
        timestamp
      })));
      const baseComments = variables_0.pendingCommentsSnapshot.filter(_temp5);
      const replyComments = variables_0.pendingCommentsSnapshot.filter(_temp6);
      for (const pendingComment of baseComments) {
        const localTransitionKey = toPendingGithubCommentId(pendingComment.id);
        const optimisticComment = buildOptimisticPromotedComment({
          pendingComment,
          inReplyToId: null,
          githubReviewId: optimisticReview.githubReviewId,
          userLogin,
          userAvatarUrl,
          timestamp,
          localTransitionKey
        });
        const optimisticThread = buildOptimisticReviewThread(optimisticComment);
        registerStableViewKey(optimisticComment.githubCommentId, prScopeKey, localTransitionKey);
        optimisticCommentIdByPendingId.set(pendingComment.id, optimisticComment.githubCommentId);
        promotedPendingComments.push({
          tempId: pendingComment.id,
          optimisticCommentId: optimisticComment.githubCommentId,
          localTransitionKey,
          threadId: optimisticThread.id
        });
        stagePrV2OptimisticComments(prScopeKey, [optimisticComment]);
        stagePrV2OptimisticThreads(prScopeKey, [optimisticThread]);
        queryClient.setQueryData(commentsKey, current_1 => appendOptimisticComment(current_1, optimisticComment));
        queryClient.setQueryData(discussionKey, current_2 => appendOptimisticTimelineItem(current_2, buildOptimisticReviewCommentTimelineItem(optimisticComment)));
        queryClient.setQueryData(reviewThreadsKey, current_3 => appendOptimisticThread(current_3, optimisticThread));
      }
      for (const pendingComment_0 of replyComments) {
        const inReplyToId = pendingComment_0.parent_is_pending ? optimisticCommentIdByPendingId.get(pendingComment_0.in_reply_to_id) ?? null : String(pendingComment_0.in_reply_to_id);
        const localTransitionKey_0 = toPendingGithubCommentId(pendingComment_0.id);
        const optimisticComment_0 = buildOptimisticPromotedComment({
          pendingComment: pendingComment_0,
          inReplyToId,
          githubReviewId: optimisticReview.githubReviewId,
          userLogin,
          userAvatarUrl,
          timestamp,
          localTransitionKey: localTransitionKey_0
        });
        registerStableViewKey(optimisticComment_0.githubCommentId, prScopeKey, localTransitionKey_0);
        optimisticCommentIdByPendingId.set(pendingComment_0.id, optimisticComment_0.githubCommentId);
        promotedPendingComments.push({
          tempId: pendingComment_0.id,
          optimisticCommentId: optimisticComment_0.githubCommentId,
          localTransitionKey: localTransitionKey_0
        });
        stagePrV2OptimisticComments(prScopeKey, [optimisticComment_0]);
        queryClient.setQueryData(commentsKey, current_4 => appendOptimisticComment(current_4, optimisticComment_0));
        queryClient.setQueryData(discussionKey, current_5 => appendOptimisticTimelineItem(current_5, buildOptimisticReviewCommentTimelineItem(optimisticComment_0)));
        queryClient.setQueryData(reviewThreadsKey, current_6 => inReplyToId ? appendReplyToThread(current_6, inReplyToId, optimisticComment_0) : current_6);
      }
      clearPendingComments(variables_0.pendingCommentsSnapshot);
      queryClient.setQueryData(summaryKey, current_7 => {
        if (!current_7) {
          return current_7;
        }
        didApplyOptimisticSummary = true;
        return removeRequestedReviewerFromSummary(current_7, userLogin);
      });
      queryClient.setQueryData(mergeabilityKey, current_8 => applyReviewToMergeability(current_8, variables_0.reviewType));
      patchInboxPullRequest(queryClient, {
        owner,
        repo,
        number: Number(number)
      }, pr => {
        const nextReviewState = variables_0.reviewType === "COMMENT" ? pr.reviewState : variables_0.reviewType === "APPROVE" ? "APPROVED" : "CHANGES_REQUESTED";
        const nextRequestedReviewers = (pr.requested_reviewers ?? []).filter(r => r.login.toLowerCase() !== userLogin.toLowerCase());
        const nextUpdatedAt = new Date().toISOString();
        return {
          ...pr,
          reviewState: nextReviewState,
          requested_reviewers: nextRequestedReviewers as typeof pr.requested_reviewers,
          reviews: [...(pr.reviews ?? []), optimisticInboxReview],
          updatedAt: nextUpdatedAt
        };
      });
      return {
        clearedPendingComments: variables_0.pendingCommentsSnapshot,
        promotedPendingComments,
        optimisticReviewId: optimisticReview.githubReviewId,
        didApplyOptimisticSummary,
        requestedReviewerToRestore,
        previousMergeability
      };
    };
    $[73] = clearPendingComments;
    $[74] = commentsKey;
    $[75] = discussionKey;
    $[76] = mergeabilityKey;
    $[77] = number;
    $[78] = owner;
    $[79] = prScopeKey;
    $[80] = queryClient;
    $[81] = repo;
    $[82] = reviewThreadsKey;
    $[83] = reviewsKey;
    $[84] = summaryKey;
    $[85] = userAvatarUrl;
    $[86] = userLogin;
    $[87] = t17;
  } else {
    t17 = $[87];
  }
  let t18;
  if ($[88] !== addPendingComment || $[89] !== commentsKey || $[90] !== discussionKey || $[91] !== installationId || $[92] !== number || $[93] !== owner || $[94] !== prScopeKey || $[95] !== pullNumber || $[96] !== queryClient || $[97] !== removeCommentText || $[98] !== repo || $[99] !== reviewThreadsKey || $[100] !== softInvalidateAfterMutation || $[101] !== startNewSubmissionIdAfterSuccess || $[102] !== summaryKey) {
    t18 = (result_0, variables_1, context) => {
      startNewSubmissionIdAfterSuccess();
      noteReviewSubmitted({
        owner,
        repo,
        number: pullNumber,
        title: queryClient.getQueryData(summaryKey)?.pullRequest.title ?? `${owner}/${repo} #${pullNumber}`
      });
      if (result_0.warnings) {
        for (const warning of result_0.warnings) {
          toast.warning(warning);
        }
      }
      const remappedReplyParentIds = buildOptimisticReplyParentIdRemap(variables_1.pendingCommentsSnapshot, context?.promotedPendingComments ?? [], result_0.idMapping);
      if (remappedReplyParentIds.size > 0) {
        queryClient.setQueryData(commentsKey, current_9 => remapOptimisticReplyParentsInComments(current_9, remappedReplyParentIds));
        queryClient.setQueryData(discussionKey, current_10 => remapOptimisticReplyParentsInDiscussion(current_10, remappedReplyParentIds));
      }
      for (const promotedPendingComment of context?.promotedPendingComments ?? []) {
        const realId = result_0.idMapping[String(promotedPendingComment.tempId)];
        if (realId != null && Number.isSafeInteger(realId) && realId > 0) {
          linkPrV2LocalCommentIds(prScopeKey, promotedPendingComment.optimisticCommentId, String(realId));
          transferStableViewKeyToId(promotedPendingComment.optimisticCommentId, String(realId));
        }
      }
      const failedTempIds = new Set(result_0.commentResults.filter(_temp7).map(_temp8));
      if (failedTempIds.size > 0 && context) {
        const failedPendingComments = variables_1.pendingCommentsSnapshot.filter(pendingComment_1 => failedTempIds.has(pendingComment_1.id));
        const failedOptimisticCommentIds = context.promotedPendingComments.filter(pendingComment_2 => failedTempIds.has(pendingComment_2.tempId)).map(_temp9);
        const failedOptimisticThreadIds = context.promotedPendingComments.filter(pendingComment_4 => failedTempIds.has(pendingComment_4.tempId)).map(_temp10).filter(_temp11);
        for (const pendingComment_6 of failedPendingComments) {
          addPendingComment(remapFailedPendingCommentParent(pendingComment_6, result_0.idMapping));
        }
        queryClient.setQueryData(commentsKey, current_11 => removeOptimisticCommentsById(current_11, failedOptimisticCommentIds));
        removePrV2LocalCommentsByLookupIds(prScopeKey, failedOptimisticCommentIds);
        queryClient.setQueryData(discussionKey, current_12 => removeOptimisticTimelineItemsById(current_12, failedOptimisticCommentIds));
        queryClient.setQueryData(reviewThreadsKey, current_13 => removeOptimisticThreadNodesByCommentId(current_13, failedOptimisticCommentIds));
        removePrV2LocalThreadsByLookupIds(prScopeKey, failedOptimisticThreadIds);
      }
      trackClientEvent("v2_pr_review_submitted", installationId?.toString(), {
        owner,
        repo,
        pr_number: number,
        review_type: variables_1.reviewType,
        pending_comments_count: variables_1.pendingCommentsSnapshot.length
      });
      removeCommentText();
      scheduleReviewFallbackInvalidation(prScopeKey, softInvalidateAfterMutation);
    };
    $[88] = addPendingComment;
    $[89] = commentsKey;
    $[90] = discussionKey;
    $[91] = installationId;
    $[92] = number;
    $[93] = owner;
    $[94] = prScopeKey;
    $[95] = pullNumber;
    $[96] = queryClient;
    $[97] = removeCommentText;
    $[98] = repo;
    $[99] = reviewThreadsKey;
    $[100] = softInvalidateAfterMutation;
    $[101] = startNewSubmissionIdAfterSuccess;
    $[102] = summaryKey;
    $[103] = t18;
  } else {
    t18 = $[103];
  }
  let t19;
  if ($[104] !== addPendingComment || $[105] !== commentsKey || $[106] !== discussionKey || $[107] !== invalidateAfterMutation || $[108] !== mergeabilityKey || $[109] !== number || $[110] !== owner || $[111] !== prScopeKey || $[112] !== queryClient || $[113] !== repo || $[114] !== reviewThreadsKey || $[115] !== reviewsKey || $[116] !== summaryKey) {
    t19 = (error_0, _variables, context_0) => {
      clearPendingOptimisticMutation({
        owner,
        repo,
        number: Number(number)
      });
      if (!context_0) {
        toast.error(error_0 instanceof Error ? error_0.message : "Failed to submit review");
        return;
      }
      for (const pendingComment_7 of context_0.clearedPendingComments) {
        addPendingComment(pendingComment_7);
      }
      const optimisticCommentIds = context_0.promotedPendingComments.map(_temp12);
      const optimisticThreadIds = context_0.promotedPendingComments.map(_temp13).filter(_temp14);
      const optimisticDiscussionIds = [context_0.optimisticReviewId, ...optimisticCommentIds];
      removePrV2LocalReviewsByLookupIds(prScopeKey, [context_0.optimisticReviewId]);
      removePrV2LocalCommentsByLookupIds(prScopeKey, optimisticCommentIds);
      removePrV2LocalThreadsByLookupIds(prScopeKey, optimisticThreadIds);
      queryClient.setQueryData(reviewsKey, current_14 => removeReviewById(current_14, context_0.optimisticReviewId));
      queryClient.setQueryData(commentsKey, current_15 => removeOptimisticCommentsById(current_15, optimisticCommentIds));
      queryClient.setQueryData(discussionKey, current_16 => removeOptimisticTimelineItemsById(current_16, optimisticDiscussionIds));
      queryClient.setQueryData(reviewThreadsKey, current_17 => removeOptimisticThreadNodesByCommentId(current_17, optimisticCommentIds));
      if (context_0.didApplyOptimisticSummary) {
        queryClient.setQueryData(summaryKey, current_18 => restoreRequestedReviewerInSummary(current_18, context_0.requestedReviewerToRestore));
      }
      if (context_0.previousMergeability !== undefined) {
        queryClient.setQueryData(mergeabilityKey, context_0.previousMergeability);
      }
      scheduleReviewFallbackInvalidation(prScopeKey, invalidateAfterMutation);
      toast.error(error_0 instanceof Error ? error_0.message : "Failed to submit review");
    };
    $[104] = addPendingComment;
    $[105] = commentsKey;
    $[106] = discussionKey;
    $[107] = invalidateAfterMutation;
    $[108] = mergeabilityKey;
    $[109] = number;
    $[110] = owner;
    $[111] = prScopeKey;
    $[112] = queryClient;
    $[113] = repo;
    $[114] = reviewThreadsKey;
    $[115] = reviewsKey;
    $[116] = summaryKey;
    $[117] = t19;
  } else {
    t19 = $[117];
  }
  let t20;
  if ($[118] !== mutationKey || $[119] !== t16 || $[120] !== t17 || $[121] !== t18 || $[122] !== t19) {
    t20 = {
      mutationKey,
      mutationFn: t16,
      onMutate: t17,
      onSuccess: t18,
      onError: t19
    };
    $[118] = mutationKey;
    $[119] = t16;
    $[120] = t17;
    $[121] = t18;
    $[122] = t19;
    $[123] = t20;
  } else {
    t20 = $[123];
  }
  const mutation = useMutation(t20);
  let t21;
  if ($[124] !== commentText || $[125] !== getOrCreateSubmissionId || $[126] !== getPendingCommentsSnapshot || $[127] !== mutation) {
    t21 = reviewType => {
      mutation.mutate({
        comment: commentText,
        reviewType,
        pendingCommentsSnapshot: getPendingCommentsSnapshot(),
        submissionId: getOrCreateSubmissionId()
      });
    };
    $[124] = commentText;
    $[125] = getOrCreateSubmissionId;
    $[126] = getPendingCommentsSnapshot;
    $[127] = mutation;
    $[128] = t21;
  } else {
    t21 = $[128];
  }
  let t22;
  if ($[129] !== getOrCreateSubmissionId || $[130] !== getPendingCommentsSnapshot || $[131] !== mutation) {
    t22 = async (comment_1, reviewType_0) => mutation.mutateAsync({
      comment: comment_1,
      reviewType: reviewType_0,
      pendingCommentsSnapshot: getPendingCommentsSnapshot(),
      submissionId: getOrCreateSubmissionId()
    });
    $[129] = getOrCreateSubmissionId;
    $[130] = getPendingCommentsSnapshot;
    $[131] = mutation;
    $[132] = t22;
  } else {
    t22 = $[132];
  }
  let t23;
  if ($[133] !== commentText || $[134] !== installationId || $[135] !== mutation.isPending || $[136] !== prPendingComments || $[137] !== removeCommentText || $[138] !== setCommentText || $[139] !== t21 || $[140] !== t22) {
    t23 = {
      reviewComment: commentText,
      setReviewComment: setCommentText,
      clearReviewComment: removeCommentText,
      prPendingComments,
      submitReview: t21,
      submitReviewAsync: t22,
      installationId,
      isPending: mutation.isPending
    };
    $[133] = commentText;
    $[134] = installationId;
    $[135] = mutation.isPending;
    $[136] = prPendingComments;
    $[137] = removeCommentText;
    $[138] = setCommentText;
    $[139] = t21;
    $[140] = t22;
    $[141] = t23;
  } else {
    t23 = $[141];
  }
  return t23;
}
function _temp14(threadId_0) {
  return typeof threadId_0 === "string" && threadId_0.length > 0;
}
function _temp13(pendingComment_9) {
  return pendingComment_9.threadId;
}
function _temp12(pendingComment_8) {
  return pendingComment_8.optimisticCommentId;
}
function _temp11(threadId) {
  return typeof threadId === "string" && threadId.length > 0;
}
function _temp10(pendingComment_5) {
  return pendingComment_5.threadId;
}
function _temp9(pendingComment_3) {
  return pendingComment_3.optimisticCommentId;
}
function _temp8(commentResult_0) {
  return Number(commentResult_0.tempId);
}
function _temp7(commentResult) {
  return commentResult.error;
}
function _temp6(comment_0) {
  return "in_reply_to_id" in comment_0;
}
function _temp5(comment) {
  return !("in_reply_to_id" in comment);
}
function _temp4(error) {
  return error instanceof Error ? error.message : "Failed to submit review";
}
function _temp3(state_1) {
  return state_1.addPendingComment;
}
function _temp2(state_0) {
  return state_0.clearPendingComments;
}
function _temp(state) {
  return state.pendingComments;
}